import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import type {
  Job,
  JobStatus,
  FactsAgentResults,
  FileUploadResponse,
} from '../types';

// Error response structure from backend
interface APIErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    reference_id?: string;
    details?: Record<string, unknown>;
  };
}

// Legacy error response structure
interface LegacyErrorResponse {
  detail: string;
}

// Callback for 401 handling — set by AuthContext to avoid circular imports
let _api401Callback: (() => void) | null = null;
export function setApi401Callback(cb: (() => void) | null) {
  _api401Callback = cb;
}
function getApi401Callback() {
  return _api401Callback;
}

const api = axios.create({
  baseURL: '/api',
  timeout: 300000, // 5 minutes for long-running jobs
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and timing
api.interceptors.request.use(
  (config) => {
    // Add request timestamp for timing
    (config as AxiosRequestConfig & { metadata?: { startTime: number } }).metadata = { startTime: Date.now() };
    // Let Axios set Content-Type automatically for FormData (multipart boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Log response time in development
    const config = response.config as AxiosRequestConfig & { metadata?: { startTime: number } };
    if (config.metadata?.startTime) {
      const duration = Date.now() - config.metadata.startTime;
      if (duration > 1000) {
        console.debug(`[API] ${config.method?.toUpperCase()} ${config.url} took ${duration}ms`);
      }
    }
    return response;
  },
  (error) => {
    // Handle 401 - Unauthorized: delegate to AuthContext instead of hard redirect
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      const onAuthFailure = getApi401Callback();
      if (onAuthFailure) {
        onAuthFailure();
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Enhanced error handler with better user messages
 */
export const handleAPIError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIErrorResponse | LegacyErrorResponse>;

    // Handle network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return 'Request timed out. Please check your connection and try again.';
      }
      if (error.code === 'ERR_NETWORK') {
        return 'Unable to connect to the server. Please check if the backend is running.';
      }
      return 'Network error. Please check your internet connection.';
    }

    const { status, data } = axiosError.response!;

    // Handle new error format
    if (data && 'error' in data && typeof data.error === 'object') {
      const errorData = data as APIErrorResponse;
      let message = errorData.error.message;

      // Add reference ID if available (for support)
      if (errorData.error.reference_id) {
        message += ` (Ref: ${errorData.error.reference_id})`;
      }

      return message;
    }

    // Handle legacy error format
    if (data && 'detail' in data) {
      return (data as LegacyErrorResponse).detail;
    }

    // Handle specific status codes
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 413:
        return 'The file is too large. Please upload a smaller file.';
      case 422:
        return 'Validation error. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Error (${status}): ${axiosError.message}`;
    }
  }

  // Handle non-Axios errors
  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get a user-friendly error code
 */
export const getErrorCode = (error: unknown): string | null => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIErrorResponse>;
    if (axiosError.response?.data && 'error' in axiosError.response.data) {
      return axiosError.response.data.error.code;
    }
  }
  return null;
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return !error.response || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
  }
  return false;
};

/**
 * Check if error is a rate limit error
 */
export const isRateLimitError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 429;
  }
  return false;
};

// ============================================================================
// FactsAgent API
// ============================================================================

export const factsAgentAPI = {
  /**
   * Upload a transcript file
   */
  uploadTranscript: async (file: File, projectId: number): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId.toString());

    const response = await api.post<FileUploadResponse>(
      '/factsagent/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  },

  /**
   * Upload transcript text content
   */
  uploadTranscriptText: async (textContent: string, projectId: number, address?: string): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('text_content', textContent);
    formData.append('project_id', projectId.toString());
    if (address) {
      formData.append('address', address);
    }

    const response = await api.post<FileUploadResponse>(
      '/factsagent/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  },

  /**
   * Start processing a transcript
   */
  startProcessing: async (jobId: number): Promise<Job> => {
    const response = await api.post<Job>(`/factsagent/process/${jobId}`);
    return response.data;
  },

  /**
   * Get job status
   */
  getStatus: async (jobId: number): Promise<JobStatus> => {
    const response = await api.get<JobStatus>(`/factsagent/status/${jobId}`);
    return response.data;
  },

  /**
   * Get job results
   */
  getResults: async (jobId: number): Promise<FactsAgentResults> => {
    const response = await api.get<FactsAgentResults>(`/factsagent/results/${jobId}`);
    return response.data;
  },

  /**
   * Download a specific step
   */
  downloadStep: (jobId: number, step: number): string => {
    const token = localStorage.getItem('auth_token') || '';
    return `/api/factsagent/download/${jobId}/${step}?token=${token}`;
  },

  /**
   * Download all outputs as ZIP
   */
  downloadAll: (jobId: number): string => {
    const token = localStorage.getItem('auth_token') || '';
    return `/api/factsagent/download-all/${jobId}?token=${token}`;
  },

  /**
   * Delete a job
   */
  deleteJob: async (jobId: number): Promise<void> => {
    await api.delete(`/factsagent/${jobId}`);
  },
};

// ============================================================================
// ICP Agent API
// ============================================================================

export const icpAgentAPI = {
  uploadFromFactsAgent: async (projectId: number, factsagentJobId: number) => {
    const formData = new FormData();
    formData.append('project_id', projectId.toString());
    formData.append('factsagent_job_id', factsagentJobId.toString());
    const response = await api.post<FileUploadResponse>('/icpagent/upload-from-factsagent', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  startGeneration: async (jobId: number) => {
    const response = await api.post(`/icpagent/generate/${jobId}`);
    return response.data;
  },

  getStatus: async (jobId: number) => {
    const response = await api.get(`/icpagent/status/${jobId}`);
    return response.data;
  },

  getResults: async (jobId: number) => {
    const response = await api.get(`/icpagent/results/${jobId}`);
    return response.data;
  },

  downloadICP: (jobId: number): string => {
    const token = localStorage.getItem('auth_token') || '';
    return `/api/icpagent/download/${jobId}?token=${token}`;
  },

  deleteJob: async (jobId: number): Promise<void> => {
    await api.delete(`/icpagent/${jobId}`);
  },
};

// ============================================================================
// Health & Status API
// ============================================================================

export const healthAPI = {
  /**
   * Check API health
   */
  checkHealth: async (): Promise<{ status: string }> => {
    const response = await api.get('/health');
    return response.data;
  },

  /**
   * Get API info
   */
  getInfo: async (): Promise<any> => {
    const response = await api.get('/');
    return response.data;
  },
};

export { api };
export default api;
